import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import {
  useUserRetrieveSuspense,
  useUsersAdminTableDataUpdate,
} from "gen/endpoints/users/users";
import { UserRoleDescriptionAPIDataList } from "gen/model/userRoleDescriptionAPIDataList";
import { UserRoleTypeEnum } from "gen/model/userRoleTypeEnum";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import TextInput from "shared/components/input/text/TextInput";

interface EditRoleModalProps {
  userId: string;
  roleDefinitions: UserRoleDescriptionAPIDataList;
  onCloseComplete: () => void;
  onSuccess: () => void;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  userId,
  roleDefinitions,
  onSuccess,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["common", "settings"]);
  const { mutateAsync: updateUser } = useUsersAdminTableDataUpdate();

  const { data: user } = useUserRetrieveSuspense(userId);

  const form = useForm<{ user_role: UserRoleTypeEnum }>({});

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("settings:user.editRole.title")}
          <ModalCloseButton />
        </ModalHeader>
        <Form
          form={form}
          onSubmit={async (data) =>
            updateUser({
              data: {
                user_id_to_role_data: { [userId]: { role: data.user_role } },
              },
            })
          }
          onSuccess={onSuccess}
          successMessage={t("settings:user.editRole.success")}
        >
          <ModalBody>
            <TextInput
              title={t("settings:user.editRole.email")}
              value={user.email}
              name="user_email"
              isReadOnly={true}
            />
            <FormSelect
              title={t("common:role")}
              name="user_role"
              defaultValue={user.role_types[0]} // Right now this UI supports a user having a single role
            >
              {useMemo(
                () =>
                  roleDefinitions.map(({ role, name }) => (
                    <SelectOption key={role} value={role}>
                      {name}
                    </SelectOption>
                  )),
                [roleDefinitions],
              )}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} />}
              confirm={
                <FormSubmitButton>{t("common:saveChanges")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default EditRoleModal;
